import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Grid, Link, Paper, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { BillOfLadingService } from '../../services/billOfLading.service';
import { ExportLcService } from '../../services/exportLc.service';
import { ExpFormService } from '../../services/expForm.service';
import { BuyerOrderService } from '../../services/buyerOrder.service';
import { PERMISSIONS } from '../../constants/permissions';
import { BillOfLadingDTO } from '../../interfaces/commercial.interface';
import { triggerDownload } from '../statutoryRegisters/shared';
import { enumLabel } from '../../utils/garmentsLabels';
import { formatDate } from '../../utils/dateUtils';

/** Bill of lading / forwarder header detail (15.3) — flat header, no child panel. Mirrors
 * ExportLcDetail's load shape, plus a "Download PDF" button (blob download). */
const BillOfLadingDetail: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { id } = useParams<{ id: string }>();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.EXP_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;

    const [bl, setBl] = useState<BillOfLadingDTO | null>(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [links, setLinks] = useState<{ label: string; text: string; path: string }[]>([]);

    const load = async () => {
        if (!id) return;
        try {
            setLoading(true);
            const loaded = await BillOfLadingService.getById(institutionId, id);
            setBl(loaded);
            // Resolve the soft references to readable numbers; a missing/forbidden one just drops out.
            const found = await Promise.all([
                loaded.exportLcId
                    ? ExportLcService.getById(institutionId, loaded.exportLcId)
                        .then(l => ({ label: 'Export LC', text: l.lcNumber, path: `/commercial/lc/${l.id}` })).catch(() => null)
                    : null,
                loaded.buyerOrderId
                    ? BuyerOrderService.getById(institutionId, loaded.buyerOrderId)
                        .then(o => ({ label: 'Buyer order', text: o.orderNumber, path: `/merchandising/orders/${o.id}` })).catch(() => null)
                    : null,
                loaded.expFormId
                    ? ExpFormService.get(institutionId, loaded.expFormId)
                        .then(x => ({ label: 'EXP form', text: x.expNumber, path: `/commercial/exp-form/${x.id}` })).catch(() => null)
                    : null,
            ]);
            setLinks(found.filter((x): x is { label: string; text: string; path: string } => !!x));
        } catch (e) {
            toast.showApiError(e, 'Failed to load bill of lading');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDownload = async () => {
        if (!bl) return;
        setDownloading(true);
        try {
            const blob = await BillOfLadingService.downloadBillOfLadingPdf(institutionId, bl.id);
            triggerDownload(blob, `bill-of-lading-${bl.blNumber}.pdf`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloading(false);
        }
    };

    if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
    if (!bl) return <Box sx={{ p: 3 }}><Typography>Bill of lading not found.</Typography></Box>;

    return (
        <Box sx={{ p: 3 }} data-help-page="commercial.blDetail.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                    <Typography variant="h5">{bl.blNumber}</Typography>
                    <Typography variant="body2" color="text.secondary">{bl.forwarderName ?? bl.carrierName ?? ''}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button data-help="commercial.blDetail.download" startIcon={<FileDownloadIcon />} variant="outlined" disabled={downloading} onClick={handleDownload}>
                        {downloading ? 'Downloading…' : 'Download PDF'}
                    </Button>
                    {canManage && (
                        <Button data-help="commercial.blDetail.edit" startIcon={<EditIcon />} variant="outlined" onClick={() => navigate(`/commercial/bills-of-lading/${bl.id}/edit`)}>
                            Edit
                        </Button>
                    )}
                </Box>
            </Box>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">B/L Date</Typography>
                        <Typography>{formatDate(bl.blDate)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Type</Typography>
                        <Typography>{enumLabel(bl.blType)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Freight Terms</Typography>
                        <Typography>{enumLabel(bl.freightTerms)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Carrier</Typography>
                        <Typography>{bl.carrierName ?? '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Vessel</Typography>
                        <Typography>{bl.vesselName ?? '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Voyage Number</Typography>
                        <Typography>{bl.voyageNumber ?? '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Container Number</Typography>
                        <Typography>{bl.containerNumber ?? '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Seal Number</Typography>
                        <Typography>{bl.sealNumber ?? '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Port of Loading</Typography>
                        <Typography>{bl.portOfLoading ?? '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Port of Discharge</Typography>
                        <Typography>{bl.portOfDischarge ?? '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Place of Delivery</Typography>
                        <Typography>{bl.placeOfDelivery ?? '—'}</Typography>
                    </Grid>
                    {links.map(l => (
                        <Grid key={l.label} size={{ xs: 6, sm: 3 }}>
                            <Typography variant="caption" color="text.secondary">{l.label}</Typography>
                            <Typography>
                                <Link component={RouterLink} to={l.path} underline="hover">{l.text}</Link>
                            </Typography>
                        </Grid>
                    ))}
                    {bl.shippingMarks && (
                        <Grid size={12}>
                            <Typography variant="caption" color="text.secondary">Shipping Marks</Typography>
                            <Typography>{bl.shippingMarks}</Typography>
                        </Grid>
                    )}
                    {bl.remarks && (
                        <Grid size={12}>
                            <Typography variant="caption" color="text.secondary">Remarks</Typography>
                            <Typography>{bl.remarks}</Typography>
                        </Grid>
                    )}
                </Grid>
            </Paper>
        </Box>
    );
};

export default BillOfLadingDetail;
